import { getDevicePixelRatio } from '@imagise/shared';

export type TConfiguration = Partial<CanvasConfiguration>;

export class CanvasConfiguration {
  browserShadowBlurConstant = 1;
  DPI = 96;
  devicePixelRatio = getDevicePixelRatio();
  perfLimitSizeTotal = 2097152;
  maxCacheSideLimit = 4096;
  minCacheSideLimit = 256;
  disableStyleCopyPaste = false;
  enableGLFiltering = true;
  textureSize = 4096;
  forceGLPutImageData = false;
  /**
   * When true, both filter backends log the duration of every applyFilters
   * call (tagged "webgl" or "canvas2d"). Intended for profiling GPU vs CPU
   * filtering in a real browser: enable via
   * `config.configure({ debugFilterTiming: true })` from devtools.
   */
  debugFilterTiming = false;
  cachesBoundsOfCurve = false;
  fontPaths: Record<string, string> = {};
  NUM_FRACTION_DIGITS = 4;

  constructor(config?: TConfiguration) {
    this.configure(config);
  }

  configure(config: TConfiguration = {}): void {
    Object.assign(this, config);
  }

  addFonts(paths: Record<string, string> = {}): void {
    this.fontPaths = {
      ...this.fontPaths,
      ...paths,
    };
  }

  removeFonts(fontFamilies: string[] = []): void {
    fontFamilies.forEach((fontFamily) => {
      delete this.fontPaths[fontFamily];
    });
  }

  clearFonts(): void {
    this.fontPaths = {};
  }

  restoreDefaults<T extends CanvasConfiguration>(keys?: (keyof T)[]): void {
    const defaults = new CanvasConfiguration() as T;
    const config =
      keys?.reduce((acc, key) => {
        acc[key] = defaults[key];
        return acc;
      }, {} as T) || defaults;

    this.configure(config);
  }
}

export const config = new CanvasConfiguration();
